import React, { useState } from "react";
import { Button, Flex, Modal, Notification, Text } from "@telia/teddy";

import { checkFwaBankIdStatus, sendFwaSigningSms, submitFwaOrder } from "../../fwaApi";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onCompleted: (orderRef: string) => void;
}

const FwaBankIdModal: React.FC<Props> = ({ isOpen, onClose, onCompleted }) => {
    const [isSendingSms, setIsSendingSms] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [smsError, setSmsError] = useState<string | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [smsSentAgain, setSmsSentAgain] = useState(false);
    const [notCompleted, setNotCompleted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleResendSms = async () => {
        setSmsError(null);
        setSmsSentAgain(false);
        setIsSendingSms(true);
        try {
            await sendFwaSigningSms();
            setSmsSentAgain(true);
        } catch (e) {
            setSmsError(e instanceof Error ? e.message : "Kunne ikke sende SMS. Prøv igjen.");
        } finally {
            setIsSendingSms(false);
        }
    };

    const handleCheckStatus = async () => {
        setStatusError(null);
        setSubmitError(null);
        setNotCompleted(false);
        setIsCheckingStatus(true);
        try {
            const result = await checkFwaBankIdStatus();
            if (result.completed) {
                try {
                    const submitResult = await submitFwaOrder();
                    onCompleted(submitResult.orderRef);
                } catch (e) {
                    setSubmitError(e instanceof Error ? e.message : "Kunne ikke fullføre ordre. Prøv igjen.");
                }
            } else {
                setNotCompleted(true);
            }
        } catch (e) {
            setStatusError(e instanceof Error ? e.message : "Kunne ikke sjekke status. Prøv igjen.");
        } finally {
            setIsCheckingStatus(false);
        }
    };

    return (
        <Modal defaultOpen open={isOpen}>
            <Modal.Overlay>
                <Modal.Content>
                    <Modal.Title>Avventer BankID fra kunden</Modal.Title>
                    <Modal.Close aria-label="lukk" slot="floating" onClick={onClose} />
                    <Modal.Description>
                        <Text>
                            En SMS med lenke til BankID-verifisering er sendt til kunden. Vent til kunden har fullført,
                            og sjekk deretter status.
                        </Text>
                    </Modal.Description>
                    {smsError && (
                        <Notification type="tile" variant="error" mt="200">
                            <Notification.Icon />
                            <Notification.Text>{smsError}</Notification.Text>
                        </Notification>
                    )}
                    {smsSentAgain && !smsError && (
                        <Notification type="tile" variant="success" mt="200">
                            <Notification.Icon />
                            <Notification.Text>SMS er sendt på nytt.</Notification.Text>
                        </Notification>
                    )}
                    {statusError && (
                        <Notification type="tile" variant="error" mt="200">
                            <Notification.Icon />
                            <Notification.Text>{statusError}</Notification.Text>
                        </Notification>
                    )}
                    {submitError && (
                        <Notification type="tile" variant="error" mt="200">
                            <Notification.Icon />
                            <Notification.Text>{submitError}</Notification.Text>
                        </Notification>
                    )}
                    {notCompleted && !statusError && (
                        <Notification type="tile" variant="information" mt="200">
                            <Notification.Icon />
                            <Notification.Text>Kunden har ikke fullført BankID ennå.</Notification.Text>
                        </Notification>
                    )}
                    <Flex direction="row" gap="200" mt="400">
                        <Button variant="secondary" onClick={handleResendSms} loading={isSendingSms}>
                            Send SMS på nytt
                        </Button>
                        <Button variant="primary" onClick={handleCheckStatus} loading={isCheckingStatus}>
                            Sjekk status nå
                        </Button>
                    </Flex>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default FwaBankIdModal;
